"use client";

import React, { useEffect } from "react";
import { MapPin, Phone, ShieldCheck, Sparkles, Truck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
export default function Intake() {
  useEffect(() => {
    const linkId = "jobber-work-request-css";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.media = "screen";
      link.href = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  return <section id="request-service" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider mb-4">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            24/7 Regional Emergency Response
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Request emergency flood and water restoration
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            Submit your details for rapid crew dispatch, or call our local team directly. Certified TriServe technicians respond across Maryland, Washington DC, and Northern Virginia without third-party call center delays.
          </p>
        </div>

        {/* Two-Column Layout: Form on Left, Dispatch Card on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Form Container */}
          <div className="lg:col-span-7">
            <Card className="border border-border shadow-md bg-card">
              <CardHeader className="border-b border-border/60 pb-5">
                <CardTitle className="text-xl font-bold text-card-foreground">
                  Direct Intake Form
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Field supervisors receive this dispatch request directly on mobile.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6">
                <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
              </CardContent>
            </Card>
          </div>

          {/* Right Column: High-Visibility Dispatch Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-xl border border-primary/20 bg-secondary text-secondary-foreground p-6 sm:p-8 shadow-lg relative overflow-hidden">
              {/* Status Indicator Bar */}
              <div className="flex items-center justify-between pb-6 border-b border-secondary-foreground/15 mb-6">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                  </span>
                  <span className="text-xs uppercase tracking-wider font-mono font-semibold text-secondary-foreground">
                    Live Dispatch Status: Active
                  </span>
                </div>
                <span className="text-xs font-mono text-secondary-foreground/70">
                  Avg. Response &lt; 60m
                </span>
              </div>

              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-accent">
                  Immediate Emergency Action
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-secondary-foreground font-default">
                  Call directly for fastest arrival
                </h3>
                <p className="text-sm text-secondary-foreground/80 leading-relaxed">
                  Water migration compounds within minutes. Speak directly with a licensed restoration lead in your area right now — no answering services, no routing queues.
                </p>
              </div>

              {/* Call-to-action button */}
              <div className="mt-8 space-y-3">
                <Button asChild className="w-full h-14 bg-accent hover:bg-accent/95 text-accent-foreground font-extrabold text-lg shadow-md cursor-pointer flex items-center justify-center gap-3 rounded-md">
                  <a href="tel:8554356631">
                    <Phone className="size-5 shrink-0" />
                    <span>855-435-6631</span>
                  </a>
                </Button>
                <div className="text-center">
                  <span className="text-xs font-mono text-secondary-foreground/70">
                    Toll-Free • 24/7/365 Emergency Dispatch
                  </span>
                </div>
              </div>

              {/* Technical Spec / Operational Points */}
              <div className="mt-8 pt-6 border-t border-secondary-foreground/15 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-md bg-secondary-foreground/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Truck className="size-4 text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-secondary-foreground">
                      No Third-Party Call Centers
                    </h4>
                    <p className="text-xs text-secondary-foreground/75 mt-0.5">
                      You speak straight with field coordinators who schedule trucks and stage extraction equipment immediately.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-md bg-secondary-foreground/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="size-4 text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-secondary-foreground">
                      Tri-State Regional Coverage
                    </h4>
                    <p className="text-xs text-secondary-foreground/75 mt-0.5">
                      Dedicated teams ready across Maryland, Washington DC, and Northern Virginia.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-md bg-secondary-foreground/10 flex items-center justify-center shrink-0 mt-0.5">
                    <ShieldCheck className="size-4 text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-secondary-foreground">
                      Direct Insurance Coordination
                    </h4>
                    <p className="text-xs text-secondary-foreground/75 mt-0.5">
                      We document moisture maps, infrared thermal imaging, and bill your carrier directly.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Credential Spec Strip */}
            <div className="rounded-lg border border-border bg-muted/50 p-4 flex items-center justify-between text-xs text-muted-foreground font-mono">
              <span className="flex items-center gap-1.5 font-medium">
                <Sparkles className="size-3.5 text-primary" />
                IICRC Certified Water Restoration
              </span>
              <span>EPA &amp; OSHA Compliant</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}